/**
 * Services Section Component
 * Display of services and products offered - fetched from CMS API
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const CATEGORY_ID = 34;

const colors = [
  { color: 'text-[#2f3951]', bgColor: 'bg-blue-50' },
  { color: 'text-green-700', bgColor: 'bg-green-50' },
  { color: 'text-purple-700', bgColor: 'bg-purple-50' },
  { color: 'text-orange-700', bgColor: 'bg-orange-50' },
  { color: 'text-cyan-700', bgColor: 'bg-cyan-50' },
  { color: 'text-red-700', bgColor: 'bg-red-50' },
];

const OUR_SERVICE_UID = 'OUR-SERVICE-SECTION';
const LEFT_OURSERVICE_UID = 'LEFT-OURSERVICE-SECTION';

/** ✅ HTML entity decoder fix */
const decodeHtml = (html) => {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const cleanText = (text) =>
  decodeHtml(text)
    ?.replace(/<[^>]*>/g, '')
    ?.replace(/\u00A0/g, ' ') || '';

// Resolve service links for specific items regardless of CMS value
const resolveServiceLink = (title, fallbackLink) => {
  if (!title) return fallbackLink;
  const normalizedTitle = title.trim().toLowerCase();
  if (normalizedTitle === 'paying bills') {
    return '/services/bill-payments';
  }
  return fallbackLink;
};

const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [sectionData, setSectionData] = useState(null);
  const [leftCardData, setLeftCardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [servicesRes, sectionRes, leftCardRes] = await Promise.allSettled([
          axios.post(
            `${CMS_API_URL}info/by-category`,
            { CategoryId: CATEGORY_ID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: OUR_SERVICE_UID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: LEFT_OURSERVICE_UID },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
          ),
        ]);

        if (servicesRes.status === 'fulfilled') {
          const res = servicesRes.value;
          if (res.data?.code === 1 && Array.isArray(res.data?.data)) {
            const filteredItems = res.data.data.filter(
              (item) => item.IsDeleted === false && item.IsPublished === true
            );

            const apiServices = filteredItems.map((item, index) => ({
              id: item.InfoId || index,
              title: item.Title || '',
              description: item.Description || '',
              icon: LucideIcons[item.Image],
              link: resolveServiceLink(item.Title, item.Link) || '/',
              ...colors[index % colors.length],
            }));

            setServices(apiServices);
          }
        }

        if (sectionRes.status === 'fulfilled') {
          const res = sectionRes.value;
          if (res.data?.code === 1 && Array.isArray(res.data?.data) && res.data.data.length > 0) {
            setSectionData(res.data.data[0]);
          }
        }

        if (leftCardRes.status === 'fulfilled') {
          const res = leftCardRes.value;
          if (res.data?.code === 1 && Array.isArray(res.data?.data) && res.data.data.length > 0) {
            setLeftCardData(res.data.data[0]);
          }
        }

      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  if (loading) {
    return (
      <section className="bg-white">
        <div className="bg-[#f8f1e5]">
          <div className="container mx-auto px-6 md:px-8 lg:px-12 py-3 md:py-4">
            <div className="text-center">
              <div className="h-4 w-24 bg-[#ca9a37]/20 rounded mx-auto animate-pulse" />
              <div className="h-8 w-3/4 bg-[#2f3951]/20 rounded mx-auto mt-2 animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (services.length === 0) return null;

  return (
    <section className="bg-white">

      {/* HEADER */}
      <div className="bg-[#f8f1e5]">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 py-3 md:py-4">
          <div className="text-center">
            <span className="text-[#ca9a37] font-semibold text-sm uppercase tracking-wide">
              {sectionData?.Title}
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-[#2f3951] mt-2 max-w-3xl mx-auto">
              {cleanText(sectionData?.Description)}
            </h2>

            <div className="w-24 h-1 bg-[#ca9a37] mx-auto mt-4"></div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="container mx-auto px-6 md:px-8 lg:px-12 py-12 md:py-7">
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* LEFT CARD */}
          <div className="lg:w-72 xl:w-80 flex-shrink-0 bg-[#2f3951] text-white rounded-xl p-8 lg:sticky lg:top-24">
            <div className="border-l-4 border-[#ca9a37] pl-4 mb-6">
              <h3 className="text-3xl lg:text-4xl font-extrabold mb-3 leading-tight">
                {leftCardData?.Title}
              </h3>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              {cleanText(leftCardData?.Description)}
            </p>

            <div className="flex items-center space-x-2">
              <div className="w-12 h-1 bg-[#ca9a37]"></div>
              <div className="w-4 h-1 bg-[#ca9a37]/50"></div>
            </div>
          </div>

          {/* SERVICES GRID */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {services.map((service) => {
              const Icon = service.icon;
              const isNewTab = ['loan products', 'member shares', 'transfer & deposit', 'saving services', 'membership'].includes(
                service.title.trim().toLowerCase()
              );

              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all border border-gray-100 hover:border-[#ca9a37]/30"
                >
                  <div className={`w-14 h-14 ${service.bgColor} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    {Icon && <Icon className={`w-7 h-7 ${service.color}`} />}
                  </div>

                  <h3 className="text-xl font-bold text-[#2f3951] mb-2">
                    {service.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-relaxed mb-4 text-justify">
                    {cleanText(service.description)}
                  </p>

                  {isNewTab ? (
                    <a
                      href={service.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-semibold text-[#2f3951] hover:text-[#ca9a37] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ca9a37] rounded"
                    >
                      READ MORE
                      <div className="ml-2 w-12 h-0.5 bg-current"></div>
                    </a>
                  ) : (
                    <Link
                      to={service.link}
                      className="inline-flex items-center text-sm font-semibold text-[#2f3951] hover:text-[#ca9a37] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ca9a37] rounded"
                    >
                      READ MORE
                      <div className="ml-2 w-12 h-0.5 bg-current"></div>
                    </Link>
                  )}
                </div>
              );
            })}

          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;